import React, { useEffect, useMemo, useState } from "react";
import { settingsApi } from "../../../api";

/* -------------------- SIMPLE MESSAGE BAR -------------------- */
function MessageBar({ message, onClose }) {
  if (!message) return null;
  return (
    <div className="alert alert-info d-flex justify-content-between align-items-center">
      <div>{message}</div>
      <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
        Close
      </button>
    </div>
  );
}

/* -------------------- SIMPLE MODAL -------------------- */
function Modal({ open, title, children, onClose }) {
  if (!open) return null;
  return (
    <div
      onMouseDown={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 9999,
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="bg-white border rounded p-3"
        style={{ width: "min(800px, 100%)", maxHeight: "90vh", overflow: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="m-0">{title}</h5>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const BusinessCategories = () => {
  useEffect(() => {
    document.title = "StarRec Business Categories";
  }, []);

  const [tab, setTab] = useState("manage"); // "add" | "manage"
  const [countries] = useState(["India", "UAE", "USA"]);

  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [rows, setRows] = useState([]); // {id, country, categoryName}

  const [filterCountry, setFilterCountry] = useState("");

  const [message, setMessage] = useState("");
  const showMessage = (txt) => {
    setMessage(txt);
    window.clearTimeout(window.__msgTimer);
    window.__msgTimer = window.setTimeout(() => setMessage(""), 2000);
  };

  const [newCountry, setNewCountry] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");

  const [viewOpen, setViewOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [editCountry, setEditCountry] = useState("");
  const [editName, setEditName] = useState("");

  const mapCategory = (c) => ({
    id: (c?._id || c?.id || "").toString(),
    country: c?.country || "",
    categoryName: c?.name || c?.categoryName || "",
    raw: c,
  });

  const loadCategories = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await settingsApi.getCategories();
      const list = Array.isArray(res) ? res : [];
      setRows(list.map(mapCategory));
    } catch (e) {
      setError(e?.message || "Failed to load business categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => (filterCountry ? r.country === filterCountry : true));
  }, [rows, filterCountry]);

  const openView = (row) => {
    setSelected(row);
    setEditCountry(row.country || "");
    setEditName(row.categoryName || "");
    setViewOpen(true);
  };

  const closeView = () => {
    setSelected(null);
    setViewOpen(false);
    setEditCountry("");
    setEditName("");
  };

  const handleSave = async () => {
    if (!newCountry) return alert("Please select Country");
    if (!newCategoryName.trim()) return alert("Please enter Category Name");

    setBusy(true);
    try {
      await settingsApi.addCategory({
        country: newCountry,
        name: newCategoryName.trim(),
      });

      showMessage("Category added successfully");
      setNewCountry("");
      setNewCategoryName("");
      setTab("manage");
      await loadCategories();
    } catch (e) {
      alert(e?.message || "Failed to add category");
    } finally {
      setBusy(false);
    }
  };

  const handleUpdate = async () => {
    if (!selected?.id) return;
    if (!editCountry) return alert("Please select Country");
    if (!editName.trim()) return alert("Please enter Category Name");

    setBusy(true);
    try {
      await settingsApi.updateCategory(selected.id, {
        country: editCountry,
        name: editName.trim(),
      });

      showMessage("Category updated successfully");
      closeView();
      await loadCategories();
    } catch (e) {
      alert(e?.message || "Failed to update category");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container py-4">
      <h1 className="fs-4 fw-bold mb-3">Business Categories</h1>

      <MessageBar message={message} onClose={() => setMessage("")} />

      {error ? (
        <div className="alert alert-danger d-flex justify-content-between align-items-center">
          <div>{error}</div>
          <button className="btn btn-sm btn-outline-danger" onClick={loadCategories}>
            Retry
          </button>
        </div>
      ) : null}

      {/* Tabs */}
      <div className="btn-group mb-3" role="group" aria-label="tabs">
        <button
          className={`btn ${tab === "add" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => setTab("add")}
        >
          Add New
        </button>
        <button
          className={`btn ${tab === "manage" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => setTab("manage")}
        >
          Manage
        </button>
      </div>

      {/* ADD NEW */}
      {tab === "add" && (
        <div className="border rounded p-3">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Country</label>
              <select
                className="form-select"
                value={newCountry}
                onChange={(e) => setNewCountry(e.target.value)}
                disabled={busy}
              >
                <option value="">Select</option>
                {countries.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label">Category Name</label>
              <input
                className="form-control"
                placeholder="Enter Category Name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>

          <div className="mt-3 d-flex justify-content-end gap-2">
            <button className="btn btn-outline-secondary" onClick={() => setTab("manage")} disabled={busy}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={busy}>
              {busy ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}

      {/* MANAGE */}
      {tab === "manage" && (
        <>
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label">Country</label>
              <select
                className="form-select"
                value={filterCountry}
                onChange={(e) => setFilterCountry(e.target.value)}
              >
                <option value="">All</option>
                {countries.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6 d-flex align-items-end justify-content-end gap-2">
              <button className="btn btn-outline-primary" onClick={loadCategories} disabled={loading}>
                {loading ? "Loading..." : "Refresh"}
              </button>
              <button className="btn btn-outline-primary" onClick={() => setTab("add")}>
                Add New
              </button>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-bordered table-striped align-middle">
              <thead>
                <tr>
                  <th>Country</th>
                  <th>Category</th>
                  <th style={{ width: 180 }} className="text-center">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={3} className="text-center">
                      Loading...
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center">
                      No categories found.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.country}</td>
                      <td>{r.categoryName}</td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => openView(r)}
                        >
                          View / Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* VIEW/EDIT MODAL */}
      <Modal open={viewOpen} onClose={closeView} title="View / Edit Category">
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Country</label>
            <select
              className="form-select"
              value={editCountry}
              onChange={(e) => setEditCountry(e.target.value)}
              disabled={busy}
            >
              <option value="">Select</option>
              {countries.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6">
            <label className="form-label">Category Name</label>
            <input
              className="form-control"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              disabled={busy}
            />
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-3">
          <button className="btn btn-outline-secondary" onClick={closeView} disabled={busy}>
            Back
          </button>

          {/* ✅ No delete API in backend, so not showing delete */}
          <button className="btn btn-primary" onClick={handleUpdate} disabled={busy}>
            {busy ? "Updating..." : "Update"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default BusinessCategories;
